'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { transformItineraryForOverview, transformItineraryForTransportLegs } from '~/components/Trip/Overview/TripItinerary';
import { useTripId } from '~/hooks/useTripId';
import type { HeroRouteRecord } from '~/server/trip/getHeroRoutesForTrip';
import type { PlaceImageRecord } from '~/server/trip/getPlaceImagesForTrip';
import type { TripOverviewItem } from '~/services/engine-ts';
import { useOverviewProgressStore } from '~/stores/tpv2/overviewProgressStore';
import { transformOverviewItems } from '../lib/transformOverviewItems';
import { makeHeroRoutesCmsKey, makePlaceImagesCmsKey } from '../overviewPage/HydratedOverview';
import { CompactItineraryScrollerCard } from './CompactItineraryScrollerCard';
import styles from './ExpandedItineraryScroller.module.css';
interface ExpandedItineraryScrollerProps {
  items: TripOverviewItem[];
  legsOnly?: boolean;
}
export interface ExpandedItineraryScrollerHandle {
  scrollToProgress: (progress: number) => void;
}
export const ExpandedItineraryScroller = forwardRef<ExpandedItineraryScrollerHandle, ExpandedItineraryScrollerProps>(function ExpandedItineraryScroller(t0, ref) {
  const $ = _c(20);
  const {
    items,
    legsOnly: t1
  } = t0;
  const legsOnly = t1 === undefined ? false : t1;
  const scrollRef = useRef(null);
  const setProgress = useOverviewProgressStore(_temp);
  const tripId = useTripId();
  const [activeId, setActiveId] = useState(null);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => ({
      scrollToProgress: progress => {
        const el = scrollRef.current;
        if (!el) {
          return;
        }
        const max = el.scrollWidth - el.clientWidth;
        if (max <= 0) {
          return;
        }
        const clamped = Math.max(0, Math.min(1, progress));
        el.scrollTo({
          left: max * clamped,
          behavior: "smooth"
        });
      }
    });
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useImperativeHandle(ref, t2, t3);
  let t4;
  if ($[2] !== setProgress) {
    t4 = cardEl => {
      const scroller = scrollRef.current;
      if (!scroller) {
        return;
      }
      const cards = Array.from(scroller.querySelectorAll("[data-card-id]"));
      const index = cards.indexOf(cardEl);
      if (index < 0 || cards.length < 2) {
        return;
      }
      const targetProgress = index / (cards.length - 1);
      const max_0 = scroller.scrollWidth - scroller.clientWidth;
      if (max_0 > 0) {
        scroller.scrollTo({
          left: max_0 * targetProgress,
          behavior: "smooth"
        });
        return;
      }
      setProgress(targetProgress);
      setActiveId(cardEl.dataset.cardId ?? null);
    };
    $[2] = setProgress;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const scrollToCard = t4;
  let t5;
  if ($[4] !== tripId) {
    t5 = makeHeroRoutesCmsKey(tripId);
    $[4] = tripId;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = {
      queryKey: t5,
      enabled: false
    };
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const {
    data: heroRoutesCms
  } = useQuery(t6);
  let t7;
  if ($[8] !== tripId) {
    t7 = makePlaceImagesCmsKey(tripId);
    $[8] = tripId;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t7) {
    t8 = {
      queryKey: t7,
      enabled: false
    };
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const {
    data: placeImagesCms
  } = useQuery(t8);
  const itineraryItems = transformOverviewItems(items, heroRoutesCms, {
    mergeLegRuns: !legsOnly,
    cmsPlaces: placeImagesCms
  });
  const {
    groups,
    returnLegId
  } = legsOnly ? transformItineraryForTransportLegs(itineraryItems) : transformItineraryForOverview(itineraryItems);
  let t9;
  if ($[12] !== setProgress) {
    t9 = () => {
      const el_0 = scrollRef.current;
      if (!el_0) {
        return;
      }
      const compute = () => {
        const max_1 = el_0.scrollWidth - el_0.clientWidth;
        const progress_0 = max_1 > 0 ? el_0.scrollLeft / max_1 : 0;
        const clamped_0 = Math.max(0, Math.min(1, progress_0));
        setProgress(clamped_0);
        const cards_0 = el_0.querySelectorAll("[data-card-id]");
        if (cards_0.length === 0) {
          setActiveId(null);
          return;
        }
        const activeIndex = Math.round(clamped_0 * (cards_0.length - 1));
        setActiveId(cards_0[activeIndex]?.dataset.cardId ?? null);
      };
      compute();
      el_0.addEventListener("scroll", compute, {
        passive: true
      });
      return () => el_0.removeEventListener("scroll", compute);
    };
    $[12] = setProgress;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  useEffect(t9, [setProgress, groups.length]);
  const t10 = styles;
  const t11 = styles;
  const t12 = groups.map(group => <div key={group.id} className={styles.group}><div className={styles.groupHeader}><span className={styles.groupDay}>{group.dayLabel}</span><span className={styles.groupDate}>{group.dateRange}</span></div><div className={styles.groupCards}>{group.items.map(item => <CompactItineraryScrollerCard key={item.id} item={item} isReturnLeg={item.id === returnLegId} isActive={item.id === activeId} onCardClick={scrollToCard} />)}</div></div>);
  let t13;
  if ($[14] !== t11.scroller || $[15] !== t12) {
    t13 = <div ref={scrollRef} className={t11.scroller}>{t12}</div>;
    $[14] = t11.scroller;
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== t10.scrollerClip || $[18] !== t13) {
    t14 = <div className={t10.scrollerClip}>{t13}</div>;
    $[17] = t10.scrollerClip;
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  return t14;
});
function _temp(s) {
  return s.setProgress;
}